import React from 'react';
import classes from './AgreementChart.module.scss';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import colors from 'scss/variables/colors.module.scss'
import { useTranslation } from 'react-i18next';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useAppSelector } from 'store/hook';
import { IAgreementItem } from 'interfaces/AnalyticTypes';
import analytics from 'api/analytics';
import { Skeleton } from 'antd';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
);

const colorOfSegments = (isDarkMode: boolean) => ({
  awaiting_approval_1: isDarkMode ? colors.primary100Dark : colors.primary100,
  rejected_approval_1: isDarkMode ? colors.errorDark : colors.error,
  approved_1: isDarkMode ? colors.excel : colors.success,

  awaiting_approval_2: isDarkMode ? colors.primary100 : colors.primarytransparent,
  rejected_approval_2: isDarkMode ? colors.error : colors.errorTransparent,
  approved_2: isDarkMode ? colors.success : colors.successTransparent,
});

export const options = {
  elements: {
    bar: {
      borderWidth: 2,
    },
  },
  responsive: true,
  plugins: {
    datalabels: {
      font: {
        size: 14,
      },
      formatter: (value: number) => {
        return value > 0 ? value : '';
      },
    },
    legend: {
      display: true,
      position: 'bottom' as const,
      labels: {
        font: {
          size: 14,
        },
      },
    },
    title: {
      display: true,
      text: 'Chart.js',
      font: {
        size: 14,
      },
    },
  },
};

type AgreementChartProps = {
  period: [string, string, string];
  choosenRoom?: string;
}

const AgreementChart = ({choosenRoom, period}: AgreementChartProps) => {
  const [size, setSize] = React.useState(0);
  const [isLoadingAgreement, setIsLoadingAgreement] = React.useState(false);
  const [agreement, setAgreement] = React.useState<IAgreementItem | null>(null);
  
  const wrapElem = React.useRef<HTMLDivElement | null>(null);
  
  const { isDarkMode, messageApi } = useAppSelector((store) => store.windowState);
  
  const { t } = useTranslation();

  const getAgreements = async (period: [string, string, string], room?: string) => {
    setIsLoadingAgreement(true);
    try {
      const agreements = await analytics.getAnalyticsAgreements({room, from: period[0], to: period[1]});  
      setAgreement(agreements.data);
    } catch (e) {
      messageApi?.error(t('Analytics.errors.agreement'), 6)
    } finally {
      setIsLoadingAgreement(false);
    };
  };

  React.useEffect(() => {
    getAgreements(period, choosenRoom);
  }, [choosenRoom, period[2]]);

  const updateSize = () => {
    if (Number(wrapElem.current?.offsetWidth) > 500) {
      setSize(500);
    } else {
      setSize(Number(wrapElem.current?.offsetWidth));
    }
  }

  React.useEffect(() => {
    if (wrapElem.current) {
      window.addEventListener('resize', updateSize);
      updateSize();
    }
    return () => window.removeEventListener('resize', updateSize);
  }, [wrapElem.current,]);

  const dataArr = Object.values(agreement || []);
  const labels = Object.keys(agreement || []);
  options.plugins.title.text = t('Analytics.agreement.title');

  const themedData = (isDarkMode: boolean) => {
    const currentColors = colorOfSegments(isDarkMode);

    return {
      labels: labels.map(key => t(`Analytics.agreement.${key}`)),
      datasets: [
        {
          datalabels: {
            color: isDarkMode ? colors.activegrey : colors.activegreydark,
          },
          label: t('Analytics.agreement.quantity'),
          data: dataArr,
          borderColor: labels.map((key: string) => currentColors[`${key}_1` as keyof typeof currentColors]),
          backgroundColor: labels.map((key: string) => currentColors[`${key}_2` as keyof typeof currentColors]),
          borderRadius: 4,
        },
      ],
    }
  };  

  return (
    <div ref={wrapElem} className={classes.agreementWrap}>
      {isLoadingAgreement
        ? <Skeleton.Avatar style={{marginTop: 20}} active={true} size={size * 0.6} shape='circle' />
        : <Doughnut options={options} data={themedData(isDarkMode)} plugins={[ChartDataLabels]}/>
      }
    </div>
  );
}

export default AgreementChart;
